import moment from 'moment'
import { ClientInfoStatus, ClientIndustries } from '../../interfaces/client'
import { ParticipationStatus } from '../../interfaces/participant'
import { LOAN_TYPES } from '../client'
import { FilterFieldType, IFilter } from './types'

export const PARTICIPANT_PORTFOLIO_REVIEW_LIST_FILTERS_CONFIG = [
  {
    field: 'clients',
    dbField: 'client_name',
    type: FilterFieldType.Autocomplete,
    isSortable: true,
    placeholder: 'Client',
    title: 'Client',
    alias: 'clientInfo',
  },
  {
    field: 'loanType',
    type: FilterFieldType.List,
    isSortable: true,
    isMultiple: true,
    placeholder: 'Deal Type',
    title: 'Deal Type',
    alias: 'clientInfo',
    dbField: 'loan_type',
    options: Object.values(LOAN_TYPES).map((value) => ({
      value,
      label: value,
    })),
    excludeColumn: true,
  },
  {
    field: 'industry',
    type: FilterFieldType.List,
    isSortable: true,
    isMultiple: true,
    placeholder: 'Industry',
    title: 'Industry',
    alias: 'clientInfo',
    dbField: 'industry',
    options: Object.values(ClientIndustries).map((value) => ({
      value,
      label: value,
    })),
    excludeColumn: true,
  },
  {
    field: 'currentAmount',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Exposure',
    aggColumn: true,
    dbField: 'MAX(participation_loan_exposure."participantExposureAmount")',
  },
  {
    field: 'loanBalance',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Loan Balance',
    aggColumn: true,
    dbField:
      '(COALESCE(MAX(loanBalances.loan_balance),0)+ COALESCE(MAX(term_loan_balances.term_loan_balance),0))',
  },
  {
    field: 'utilization',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: 'Utilization',
    aggColumn: true,
    dbField: `MAX(CASE WHEN "clientInfo".client_status = '${ClientInfoStatus.Current}' THEN loanBalances.loan_balance ELSE 0 END)/NULLIF(MAX(CASE WHEN "clientInfo".client_status = '${ClientInfoStatus.Current}' THEN clientCollaterals.final_net_availability ELSE 0 END), 0)`,
  },
  {
    field: 'clientParticipantStatus',
    dbField: 'termination_date',
    type: FilterFieldType.List,
    isMultiple: true,
    isSortable: true,
    options: [
      { value: 'all', label: 'All' },
      { value: ParticipationStatus.Active, label: ParticipationStatus.Active },
      { value: ParticipationStatus.Terminated, label: 'Past' },
    ],
    placeholder: 'Status',
    title: 'Status',
    excludeColumn: true,
    excludeFullSearchFilter: true,
  },
] as const satisfies IFilter[]

export const PARTICIPANT_PARTICIPATION_FILTERS_CONFIG = [
  {
    field: 'clientName',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Client',
    alias: 'participation',
    dbField: 'client_name',
  },
  {
    field: 'loanBalance',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: 'Loan Balance',
    dbField: 'loan_balance',
    alias: 'participationLoanExposure',
  },
  {
    field: 'participantExposureAmount',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: 'Exposure',
    dbField: 'participant_exposure_amount',
    alias: 'participationLoanExposure',
  },

  {
    field: 'participantExposurePercentage',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: 'Exposure %',
    dbField: 'participant_exposure_percentage',
    alias: 'participationLoanExposure',
  },
  {
    field: 'loanExposure',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: 'Contractual Exposure',
    dbField: 'loan_exposure',
  },
  {
    field: 'interestOverride',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: 'Interest Override',
    dbField: 'interest_override',
    alias: 'participation',
  },
  {
    field: 'contractDate',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Contract Date',
    dbField: 'contract_date',
    alias: 'participation',
  },
] as const satisfies IFilter[]

export const PARTICIPANT_PARTICIPATION_OUTSTANDING_WIRES_FILTERS_CONFIG = [
  {
    field: 'clientName',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Client',
    dbField: 'client_name',
    alias: 'outstanding_wires',
  },
  {
    field: 'type',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Type',
  },
  {
    field: 'participantExposureAmount',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Exposure',
    dbField: 'participant_exposure_amount',
  },
  {
    field: 'participantExposurePercentage',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: 'Exposure %',
    dbField: 'participant_exposure_percentage',
  },
  {
    field: 'wireAmount',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Wire Amount',
    dbField: 'amount',
  },
  {
    field: 'recordDate',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Date',
    dbField: 'record_date',
    isExternalField: true,
  },
  {
    field: 'direction',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Direction',
    excludeColumn: true,
  },
  {
    type: FilterFieldType.QuickFilter,
    field: 'quickFilter',
    quickFilters: [
      {
        title: 'Last 30 Days',
        filters: {
          recordDateFrom: moment().subtract(1, 'month').format('YYYY-MM-DD'),
          recordDateTo: moment().format('YYYY-MM-DD'),
        },
      },
    ],
  },
] as const satisfies IFilter[]

export const PARTICIPATION_OUTSTANDING_WIRES_FILTERS_CONFIG = [
  {
    field: 'participant',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Participant',
    dbField: 'participant',
    alias: 'outstanding_wires',
  },
  {
    field: 'clientName',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Client',
    dbField: 'client_name',
    alias: 'outstanding_wires',
  },
  {
    field: 'participantExposureAmount',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Participant Exposure',
    dbField: 'participant_exposure_amount',
  },
  {
    field: 'participantExposurePercentage',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: 'Participant Exposure %',
    dbField: 'participant_exposure_percentage',
  },

  {
    field: 'wireAmount',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Wire Amount',
    dbField: 'amount',
  },
  {
    field: 'recordDate',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Date',
    dbField: 'record_date',
    isExternalField: true,
  },
  {
    field: 'direction',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Direction',
    excludeColumn: true,
  },
  {
    field: 'type',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Type',
  },
  {
    field: 'action',
    type: FilterFieldType.Empty,
    title: 'Action',
    align: 'right',
  },
  {
    type: FilterFieldType.QuickFilter,
    field: 'quickFilter',
    quickFilters: [
      {
        title: 'Today',
        filters: {
          recordDateFrom: moment().format('YYYY-MM-DD'),
          recordDateTo: moment().format('YYYY-MM-DD'),
        },
      },
    ],
  },
] as const satisfies IFilter[]

export const PARTICIPANTS_TABLE_FILTERS_CONFIG = [
  {
    field: 'action',
    title: '',
    type: FilterFieldType.Empty,
  },
  {
    field: 'participant',
    alias: 'participantInfo',
    type: FilterFieldType.Text,
    title: 'Participant',
    isSortable: true,
    align: 'left',
  },
  {
    field: 'totalNFE',
    type: FilterFieldType.Amount,
    title: 'Total NFE',
    isSortable: true,
    align: 'right',
    aggColumn: true,
    dbField: `SUM(DISTINCT participationLoanExposure.participant_exposure_amount)`,
    excludeFullSearchFilter: true,
  },
  {
    field: 'totalClients',
    type: FilterFieldType.Number,
    title: 'Total Clients',
    isSortable: true,
    align: 'right',
    aggColumn: true,
    dbField: `COUNT(DISTINCT participations.id)`,
    excludeFullSearchFilter: true,
  },
] as const satisfies IFilter[]

export const PARTICIPANTS_DETAIL_TABLE_FILTERS_CONFIG = [
  {
    field: 'client_name',
    type: FilterFieldType.Text,
    title: 'Client',
    isSortable: true,
    align: 'left',
    alias: 'participation',
  },
  {
    field: 'NFE',
    type: FilterFieldType.Amount,
    title: 'NFE',
    isSortable: true,
    align: 'right',
    aggColumn: true,
    alias: 'participationLoanExposure',
    dbField: `participant_exposure_amount`,
    excludeFullSearchFilter: true,
  },
  {
    field: 'loanBalance',
    type: FilterFieldType.Amount,
    title: 'ABL Loan Balance',
    isSortable: true,
    align: 'right',
    aggColumn: true,
    alias: 'participationLoanExposure',
    dbField: `loan_balance`,
    excludeFullSearchFilter: true,
  },
  {
    field: 'loanExposure',
    type: FilterFieldType.Percent,
    title: 'Target Exposure',
    isSortable: true,
    align: 'right',
    aggColumn: true,
    dbField: 'loan_exposure',
    excludeFullSearchFilter: true,
  },
] as const satisfies IFilter[]
